import {
  GET_COURSES,
  GET_COURSE_BY_ID,
  SAVE_COURSE,
  DELETE_COURSE,
  CLONE_COURSE,
  SUBMIT_TRAINING_EVENT,
  DELETE_TRAINING_EVENT,
  UPDATE_BATCH,
  CREATE_BATCH,
  DUPLICATE_BATCH,
  BULK_CREATE_TRAININGS,
  BULK_CREATE_HISTORY_TRAININGS,
  DOWNLOAD_TEMPLATE,
  DOWNLOAD_HISTORY_CATALOG_TEMPLATE,
  SEARCH_EMPLOYEES,
  SYNC_EMPLOYEE_ON_DEMAND,
  ADD_PARTICIPANT,
  GET_PARTICIPANTS
} from '@/api/master-data/course-catalog/course-catalog';
import { getBatchMutationErrorMessage } from '@/utils/error-message';

const assertBatchCreated = (response, fallbackMessage) => {
  const status = response?.status;
  const httpCode = response?.data?.httpCode;
  const statusCode = httpCode ?? status;
  const isCreated = [200, 201].includes(status) || [200, 201].includes(httpCode);

  if (!isCreated || Number(statusCode) >= 400) {
    const message = response?.data?.error
      || response?.data?.message
      || fallbackMessage;
    const error = new Error(message);
    error.response = response;
    throw error;
  }

  return response.data;
};

const state = {
  courses: [],
  courseDetail: null,
  totalItems: 0,
  loading: false,
  error: null,
  searchedEmployees: [],
  isSearching: false,
  participants: [],
  participantsLoading: false
};

const mutations = {
  SET_COURSES(state, data) {
    state.courses = data.data || [];
    state.totalItems = data.total || 0;
  },
  SET_COURSE_DETAIL(state, data) {
    state.courseDetail = data;
  },
  SET_LOADING(state, status) {
    state.loading = status;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  CLEAR_COURSE_DETAIL(state) {
    state.courseDetail = null;
  },
  SET_SEARCHED_EMPLOYEES(state, employees) {
    state.searchedEmployees = Array.isArray(employees) ? employees : [];
  },
  SET_IS_SEARCHING(state, status) {
    state.isSearching = status;
  },
  SET_PARTICIPANTS(state, participants) {
    state.participants = Array.isArray(participants) ? participants : [];
  },
  SET_PARTICIPANTS_LOADING(state, status) {
    state.participantsLoading = status;
  }
};

const actions = {
  async getCourses({ commit }, params) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await GET_COURSES(params);
      commit('SET_COURSES', response.data.data);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch courses';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCourseById({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      // Support calling with either an id or a payload { id, params }
      let courseId = id;
      let params = undefined;
      if (id && typeof id === 'object' && id.id) {
        courseId = id.id;
        params = id.params;
      }

      const response = await GET_COURSE_BY_ID(courseId, params);
      commit('SET_COURSE_DETAIL', response.data.data);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch course detail';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async saveCourse({ commit }, formData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await SAVE_COURSE(formData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save course';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteCourse({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await DELETE_COURSE(id);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete course';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async cloneCourse({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await CLONE_COURSE(id);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to clone course';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async submitTrainingEvent({ commit }, { id, data }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await SUBMIT_TRAINING_EVENT(id, data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to submit training event';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteTrainingEvent({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await DELETE_TRAINING_EVENT(id);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete training event';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateBatch({ commit }, { id, data }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await UPDATE_BATCH(id, data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update batch';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createBatch({ commit }, { trainingId, data }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await CREATE_BATCH(trainingId, data);
      return assertBatchCreated(response, 'Batch belum berhasil dibuat');
    } catch (error) {
      const errorMessage = getBatchMutationErrorMessage(error, 'Batch belum berhasil dibuat');
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async duplicateBatch({ commit }, trainingEventId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await DUPLICATE_BATCH(trainingEventId);
      return assertBatchCreated(response, 'Batch belum berhasil diduplikasi');
    } catch (error) {
      const errorMessage = getBatchMutationErrorMessage(error, 'Batch belum berhasil diduplikasi');
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async bulkCreateTrainings({ commit }, formData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await BULK_CREATE_TRAININGS(formData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to upload training file';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async bulkCreateHistoryTrainings({ commit }, formData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await BULK_CREATE_HISTORY_TRAININGS(formData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to upload history catalog file';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async downloadTemplate({ commit }) {
    try {
      const response = await DOWNLOAD_TEMPLATE();
      return response;
    } catch (error) {
      console.error('Error downloading template:', error);
      throw error;
    }
  },

  async downloadHistoryCatalogTemplate({ commit }) {
    try {
      const response = await DOWNLOAD_HISTORY_CATALOG_TEMPLATE();
      return response;
    } catch (error) {
      console.error('Error downloading history catalog template:', error);
      throw error;
    }
  },

  async searchEmployees({ commit }, searchQuery) {
    if (!searchQuery || searchQuery.length < 2) {
      commit('SET_SEARCHED_EMPLOYEES', []);
      return;
    }

    commit('SET_IS_SEARCHING', true);
    try {
      const response = await SEARCH_EMPLOYEES(searchQuery);
      
      if (response.data && response.data.data) {
        const employees = Array.isArray(response.data.data) ? response.data.data : [];
        commit('SET_SEARCHED_EMPLOYEES', employees);
        return employees;
      } else {
        commit('SET_SEARCHED_EMPLOYEES', []);
        return [];
      }
    } catch (error) {
      console.error('Error searching employees:', error);
      commit('SET_SEARCHED_EMPLOYEES', []);
      throw error;
    } finally {
      commit('SET_IS_SEARCHING', false);
    }
  },

  async syncEmployeeOnDemand({ commit }, nik) {
    const response = await SYNC_EMPLOYEE_ON_DEMAND(nik);
    const result = response?.data?.data || null;

    if (result?.employee) {
      commit('SET_SEARCHED_EMPLOYEES', [result.employee]);
    }

    return result;
  },

  async addParticipant({ commit }, { trainingEventId, data }) {
    commit('SET_PARTICIPANTS_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await ADD_PARTICIPANT(trainingEventId, data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add participant';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_PARTICIPANTS_LOADING', false);
    }
  },

  async getParticipants({ commit }, { trainingEventId, params }) {
    commit('SET_PARTICIPANTS_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const response = await GET_PARTICIPANTS(trainingEventId, params);
      if (response.data && response.data.data) {
        commit('SET_PARTICIPANTS', response.data.data);
        return response.data.data;
      }
      return [];
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to get participants';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_PARTICIPANTS_LOADING', false);
    }
  }
};

const getters = {
  getCourses: state => state.courses,
  getCourseDetail: state => state.courseDetail,
  getTotalItems: state => state.totalItems,
  getLoading: state => state.loading,
  getError: state => state.error,
  getSearchedEmployees: state => state.searchedEmployees,
  getIsSearching: state => state.isSearching,
  getParticipants: state => state.participants,
  getParticipantsLoading: state => state.participantsLoading
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
